import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Chip, CircularProgress,
  Grid, IconButton, Tooltip, Typography,
} from '@mui/material';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { AutoAwesome, MonitorHeart } from '@mui/icons-material';
import { attendanceApi } from '../services/hrmsApi';
import { useChatContext } from '../context/ChatContext';

// ── Risk colour helpers ───────────────────────────────────────────────────────
const RISK = {
  high:   { bg: '#FFEBEE', text: '#C62828', chipBg: '#FFCDD2', label: 'High' },
  medium: { bg: '#FFF8E1', text: '#F57F17', chipBg: '#FFE082', label: 'Medium' },
  low:    { bg: '#F1F8E9', text: '#388E3C', chipBg: '#C5E1A5', label: 'Low' },
};

function barColor(pct) {
  if (pct >= 26) return '#EF5350';
  if (pct >= 22) return '#FFA726';
  return '#66BB6A';
}

function riskBarColor(score) {
  if (score >= 70) return '#EF5350';
  if (score >= 50) return '#FFA726';
  if (score >= 30) return '#FFC107';
  return '#66BB6A';
}

// ── Stub data ─────────────────────────────────────────────────────────────────
const STUB = {
  burnoutSummary: 'Finance and IT show elevated burnout signals. Monday leave is 24% (vs. 20% baseline) and Friday is 27%, indicating disengagement clustering at week boundaries.',
  dayOfWeekDistribution: [
    { day: 'Mon', count: 145, pct: 24 },
    { day: 'Tue', count: 98,  pct: 16 },
    { day: 'Wed', count: 95,  pct: 16 },
    { day: 'Thu', count: 102, pct: 17 },
    { day: 'Fri', count: 162, pct: 27 },
  ],
  departmentRisks: [
    { department: 'Finance',     avgLeaveDaysPerMonth: 2.8, sickLeavePct: 48, monFriRatio: 1.62, consecutiveLeaveIncidents: 5, burnoutRiskScore: 74, riskLevel: 'high',   topSignal: 'High sick leave ratio + Monday clustering' },
    { department: 'IT',          avgLeaveDaysPerMonth: 2.4, sickLeavePct: 41, monFriRatio: 1.48, consecutiveLeaveIncidents: 3, burnoutRiskScore: 62, riskLevel: 'high',   topSignal: 'Friday leave spike — possible disengagement' },
    { department: 'Operations',  avgLeaveDaysPerMonth: 2.1, sickLeavePct: 35, monFriRatio: 1.31, consecutiveLeaveIncidents: 4, burnoutRiskScore: 48, riskLevel: 'medium', topSignal: 'Consecutive sick leave incidents trending up' },
    { department: 'Sales',       avgLeaveDaysPerMonth: 1.9, sickLeavePct: 28, monFriRatio: 1.22, consecutiveLeaveIncidents: 2, burnoutRiskScore: 35, riskLevel: 'medium', topSignal: 'Leave frequency elevated vs. Q2 baseline' },
    { department: 'Engineering', avgLeaveDaysPerMonth: 1.6, sickLeavePct: 25, monFriRatio: 1.15, consecutiveLeaveIncidents: 2, burnoutRiskScore: 24, riskLevel: 'low',    topSignal: 'Within normal range' },
    { department: 'HR',          avgLeaveDaysPerMonth: 1.4, sickLeavePct: 22, monFriRatio: 1.08, consecutiveLeaveIncidents: 1, burnoutRiskScore: 18, riskLevel: 'low',    topSignal: 'Within normal range' },
  ],
};

// ── Custom tooltip for day-of-week chart ─────────────────────────────────────
function DowTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1, fontSize: '0.78rem', boxShadow: 2 }}>
      <Typography variant="caption" fontWeight={700}>{d?.day}</Typography>
      <Box sx={{ color: 'text.secondary', mt: 0.25 }}>
        {d?.pct}% of weekly leave ({d?.count} incidents)
      </Box>
      <Box sx={{ color: '#1565C0', fontSize: '0.7rem' }}>Baseline: 20%</Box>
    </Box>
  );
}

// ── Custom tooltip for department risk chart ──────────────────────────────────
function RiskTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1, fontSize: '0.78rem', boxShadow: 2 }}>
      <Typography variant="caption" fontWeight={700}>{d?.department}</Typography>
      <Box sx={{ color: 'text.secondary', mt: 0.25 }}>Risk score: <strong>{d?.burnoutRiskScore}</strong>/100</Box>
      <Box sx={{ color: 'text.secondary' }}>{d?.topSignal}</Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LeaveBurnoutPanel() {
  const { openWithMessage } = useChatContext();
  const [data,    setData]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    attendanceApi.getLeavePatterns()
      .then(setData)
      .catch(() => setData(STUB))
      .finally(() => setLoading(false));
  }, []);

  const dow       = data?.dayOfWeekDistribution ?? [];
  const deptRisks = [...(data?.departmentRisks ?? [])].sort((a, b) => b.burnoutRiskScore - a.burnoutRiskScore);
  const atRisk    = deptRisks.filter(d => d.riskLevel !== 'low');

  const handleGlobalAskAI = () => {
    const highRisk = deptRisks.filter(d => d.riskLevel === 'high').map(d => d.department);
    openWithMessage(
      `Our leave pattern analysis shows burnout signals: ${highRisk.length > 0 ? highRisk.join(' and ') + ' are high-risk' : 'no departments in high-risk tier'}. ${data?.burnoutSummary ?? ''} What are the most effective interventions HR can deploy immediately to address burnout risk?`
    );
  };

  const handleDeptAskAI = (dept) => {
    openWithMessage(
      `${dept.department} has a burnout risk score of ${dept.burnoutRiskScore}/100 (${dept.riskLevel} risk). Key signals: ${dept.topSignal}. Avg leave: ${dept.avgLeaveDaysPerMonth} days/month, ${dept.sickLeavePct}% sick leave, Mon/Fri ratio ${dept.monFriRatio}×. What is driving this pattern and what targeted interventions would help?`
    );
  };

  return (
    <Card>
      <CardContent>
        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <MonitorHeart sx={{ fontSize: 18, color: '#E65100' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                Leave Pattern / Burnout Intelligence
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Day-of-week clustering + sick leave ratio + Mon/Fri deviation
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Ask AI about the overall burnout picture">
            <IconButton size="small" onClick={handleGlobalAskAI} disabled={loading}
              sx={{ color: 'primary.main', opacity: 0.55, '&:hover': { opacity: 1 }, flexShrink: 0 }}>
              <AutoAwesome sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── AI Summary ── */}
        {!loading && data?.burnoutSummary && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: 'action.hover', borderRadius: 1.5, px: 1.5, py: 1, mb: 2 }}>
            <AutoAwesome sx={{ fontSize: 14, color: 'primary.main', mt: 0.2, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {data.burnoutSummary}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <>
            {/* ── Charts ── */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Day-of-week distribution */}
              <Grid item xs={12} md={5}>
                <Typography variant="body2" fontWeight={700} gutterBottom>Leave by Day of Week</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Baseline: 20% per day. Orange/red bars signal clustering.
                </Typography>
                <ResponsiveContainer width="100%" height={185}>
                  <BarChart data={dow} margin={{ top: 4, right: 44, left: -22, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 35]} />
                    <RechartTooltip content={<DowTooltip />} />
                    <ReferenceLine y={20} stroke="#1565C0" strokeDasharray="5 4"
                      label={{ value: '20%', position: 'right', fontSize: 9, fill: '#1565C0' }} />
                    <Bar dataKey="pct" name="Leave %" radius={[5, 5, 0, 0]}>
                      {dow.map((entry, i) => (
                        <Cell key={i} fill={barColor(entry.pct)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              {/* Department burnout risk scores */}
              <Grid item xs={12} md={7}>
                <Typography variant="body2" fontWeight={700} gutterBottom>Burnout Risk Score by Department</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Composite: sick leave ratio · Mon/Fri clustering · consecutive incidents (0–100)
                </Typography>
                <ResponsiveContainer width="100%" height={185}>
                  <BarChart layout="vertical" data={deptRisks} margin={{ top: 4, right: 36, left: 68, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={66} />
                    <RechartTooltip content={<RiskTooltip />} />
                    <ReferenceLine x={30} stroke="#FFC107" strokeDasharray="4 3" />
                    <ReferenceLine x={50} stroke="#FFA726" strokeDasharray="4 3" />
                    <ReferenceLine x={70} stroke="#EF5350" strokeDasharray="4 3" />
                    <Bar dataKey="burnoutRiskScore" name="Burnout Risk" radius={[0, 5, 5, 0]}>
                      {deptRisks.map((entry, i) => (
                        <Cell key={i} fill={riskBarColor(entry.burnoutRiskScore)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>

            {/* ── Risk detail list (medium + high only) ── */}
            {atRisk.length > 0 && (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.68rem', display: 'block', mb: 1 }}>
                  Action Needed
                </Typography>
                {atRisk.map((dept) => {
                  const rs = RISK[dept.riskLevel] || RISK.medium;
                  return (
                    <Box key={dept.department} sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25,
                      borderBottom: '1px solid', borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={700}>{dept.department}</Typography>
                          <Chip label={rs.label} size="small"
                            sx={{ bgcolor: rs.chipBg, color: rs.text, fontWeight: 700, fontSize: '0.68rem', height: 20, '& .MuiChip-label': { px: 0.75 } }} />
                          <Typography variant="caption" color="text.disabled">
                            Score {dept.burnoutRiskScore}/100
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {dept.avgLeaveDaysPerMonth} days/mo avg · {dept.sickLeavePct}% sick leave · Mon/Fri ratio {dept.monFriRatio}× · {dept.consecutiveLeaveIncidents} consecutive incidents
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, fontStyle: 'italic' }}>
                          {dept.topSignal}
                        </Typography>
                      </Box>
                      <Tooltip title={`Ask AI about ${dept.department} burnout`}>
                        <IconButton size="small" onClick={() => handleDeptAskAI(dept)}
                          sx={{ flexShrink: 0, color: 'primary.main', opacity: 0.45, '&:hover': { opacity: 1 }, p: 0.4 }}>
                          <AutoAwesome sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
